import {
  DateRangePicker,
  FormControl,
  FormHelperText,
  Option,
  Search,
  Select,
  TextField,
} from "@deere/fuel-react";
import { InputAdornment, OutlinedInput } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import React, { memo, useState, useEffect } from "react";
import { getDateLimit, validateField } from "../utils/utils";
import { CalendarMonth } from "@mui/icons-material";
import { ROUTE_DM } from "../constants/constant";
import { Link } from "react-router-dom/cjs/react-router-dom.min";

const Field = (props) => {
  let isDate = props.type === "date";
  let [isError, defaultMsg] = validateField(
    props,
    isDate,
    props.recordData,
    props.item,
  );
  const [error, setError] = useState(isError);
  const [msg, setMsg] = useState(defaultMsg);

  useEffect(() => {
    let [isError, defaultMsg] = validateField(
      props,
      isDate,
      props.recordData,
      props.item,
    );
    if (defaultMsg !== msg) setMsg(defaultMsg);
    if (isError !== error) setError(isError);
  }, [props, error, isDate, msg]);

  const onChange = (event) => {
    let value = "";

    if (event && event.$isDayjsObject) {
      value = dayjs(event).format("YYYY-MM-DD").toString();
    } else if (event && event.constructor.name !== "Array") {
      value = event.target.value;
    }

    props.onChange(value);
  };

  const multiOnChange = (event) => {
    const options = [...event.target.selectedOptions];
    const values = options.map((option) => option.value);

    props.onMultiChange(values);
  };

  const onChangeDatePicker = (event) => {
    const value = event.map((e) => dayjs(e).format("YYYY-MM-DD").toString());
    props.onChange(value);
  };

  const selectField = () => {
    let options;
    let showAsDropdown = true;

    // Prefer explicitly provided options first
    if (props.options && props.options.length > 0) {
      options = props.options;
    } else if (props.view === "BP_VIEW") {
      if (props.recordData?.newReasonCode && props.optionsBP?.includes(props.recordData.newReasonCode)) {
        options = props.optionsBP;
      } else {
        showAsDropdown = false;
      }
    } else {
      options = props.optionsCCM;
    }

    if (!showAsDropdown) {
      return (
        <div style={{ padding: '8px 0' }}>
          {props.value || ''}
        </div>
      );
    }

    const currentValue = props.value;
    if (currentValue && options && !options.includes(currentValue)) {
      options = [currentValue, ...options];
    }

    return (
      <FormControl>
        <Select
          error={error}
          helperText={msg}
          aria-describedby="my-helper-text"
          onChange={onChange.bind(this)}
          value={props.value}
        >
          {options?.map((item) => (
            <Option key={item} value={item}>
              {" "}
              {item}{" "}
            </Option>
          ))}
        </Select>

        {msg ? (
          <FormHelperText error={error} id="my-helper-text">
            {msg}
          </FormHelperText>
        ) : null}
      </FormControl>
    );
  };

  const multiSelectField = () => {
    return (
      <FormControl>
        <Select
          multiple={true}
          disabled={props.disabled}
          error={props.error}
          value={props.value}
          onChange={multiOnChange}
          aria-describedby="my-helper-text"
        >
          {props.options?.map((item) => (
            <Option key={item} value={item}>
              {item}
            </Option>
          ))}
        </Select>
        {msg ? (
          <FormHelperText error={error} id="my-helper-text">
            {msg}
          </FormHelperText>
        ) : null}
      </FormControl>
    );
  };

  const textField = () => (
    <TextField
      error={error}
      onChange={onChange.bind(this)}
      value={props.value}
      helperText={msg}
      placeholder={props.placeholder}
    />
  );

  const numberField = () => (
    <TextField
      error={error}
      type="number"
      lang="de-DE"
      onChange={onChange.bind(this)}
      value={props.value}
      helperText={msg}
      placeholder={props.placeholder}
    />
  );

  const handleKeyDown = (event) => {
    if (event.key === "Tab" || event.key === "Enter") {
      props.onChange(event.target.value, event);
    }
  };

  const dateField = () => {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          minDate={
            props.attr?.msg?.min
              ? null
              : getDateLimit(props.attr.validation.min, props.recordData)
          }
          maxDate={
            props.attr?.msg?.max
              ? null
              : getDateLimit(props.attr.validation.max, props.recordData)
          }
          format="DD-MM-YYYY"
          onChange={onChange.bind(this)}
          value={
            dayjs.isDayjs(props.value)
              ? props.value
              : dayjs(props.value, "YYYY-MM-DD")
          }
          slotProps={{
            textField: {
              onKeyDown: handleKeyDown,
            },
          }}
        />
        {msg ? (
          <FormHelperText error={error} id="my-helper-text">
            {msg}
          </FormHelperText>
        ) : null}
      </LocalizationProvider>
    );
  };

  const textAreaField = () => (
    <TextField
      error={error}
      minRows={3}
      multiline
      value={props.value}
      onChange={onChange.bind(this)}
      type="text"
      helperText={msg}
    />
  );

  const currencyField = () => {
    return (
      <FormControl>
        <OutlinedInput
          error={error}
          type="number"
          value={props.value}
          onChange={onChange.bind(this)}
          helperText={msg}
          endAdornment={
            <InputAdornment position="end">
              {props.endPlaceHolder}
            </InputAdornment>
          }
          aria-describedby="outlined-weight-helper-text"
          inputProps={{ "aria-label": "weight" }}
        />
        {msg ? (
          <FormHelperText error={error} id="my-helper-text">
            {msg}
          </FormHelperText>
        ) : null}
      </FormControl>
    );
  };

  const linkField = () => {
    return (
      <Link
        to={{
          pathname: ROUTE_DM,
          search: `?baseCode=${encodeURIComponent(props.name)}&autoAdd=true&isProduct=true`,
          state: { isProduct: "true", newBaseCode: props.name },
        }}
        underline="always"
      >
        {props.name}
      </Link>
    );
  };

  const searchField = () => (
    <Search
      disabled={props.disabled}
      error={error}
      searchButton={false}
      size="medium"
      onChange={onChange.bind(this)}
      value={props.value}
      helperText={msg}
      placeholder={props.placeholder}
    />
  );

  const searchDateRangeField = () => {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateRangePicker
          format="dd-mm-yyy"
          onChange={onChangeDatePicker.bind(this)}
          EndInputProps={{
            startAdornment: (
              <InputAdornment position="end">
                <CalendarMonth />
              </InputAdornment>
            ),
            style: props.customWidth ? { marginRight: '16px' } : {}
          }}
          StartInputProps={{
            startAdornment: (
              <InputAdornment position="end">
                <CalendarMonth />
              </InputAdornment>
            ),
          }}
          startDatePlaceholder={props.customWidth ? "Start Date" : "Select Start Date"}
          endDatePlaceholder={props.customWidth ? "End Date" : "Select End Date"}
          dualMonth="true"
          size="medium"
          disabled={props.disabled}
        />
      </LocalizationProvider>
    );
  };

  const renderField = (type) => {
    if (type === "select") {
      return selectField();
    } else if (type === "multiSelect") {
      return multiSelectField();
    } else if (type === "text") {
      return textField();
    } else if (type === "number") {
      return numberField();
    } else if (type === "date") {
      return dateField();
    } else if (type === "text-area") {
      return textAreaField();
    } else if (type === "search") {
      return searchField();
    } else if (type === "currency") {
      return currencyField();
    } else if (type === "search-date") {
      return searchDateRangeField();
    } else if (type === "link") {
      return linkField();
    }
  };

  return (
    <div>
      {props.type === "search-date" ? (
        <div
          style={{ display: "flex", flexDirection: "column", width: props.customWidth ? "100%" : "25rem" }}
        >
          {props.label ? <label>{props.label}</label> : null}
          {renderField(props.type)}
        </div>
      ) : (
        <div
          style={{ display: "flex", flexDirection: "column", width: "15rem" }}
        >
          {props.label ? <label>{props.label}</label> : null}
          {renderField(props.type)}
        </div>
      )}
    </div>
  );
};

export default memo(Field);
